import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail, Smartphone, Monitor, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import axiosInstance from '../../api/axios';
import AdminPageHeader from '../../components/AdminPageHeader';
import { toast } from 'sonner';

const TEMPLATES = [
  {
    key: 'order-confirmation',
    name: 'Bestellbestätigung',
    description: 'E-Mail an den Kunden nach erfolgreicher Bestellung',
    endpoint: '/email-preview/order-confirmation',
  },
  {
    key: 'registration',
    name: 'Registrierung',
    description: 'Willkommens-E-Mail mit optionalem Rabattcode',
    endpoint: '/email-preview/registration',
  },
  {
    key: 'password-reset',
    name: 'Passwort zurücksetzen',
    description: '6-stelliger Code zum Setzen eines neuen Passworts',
    endpoint: '/email-preview/password-reset',
  },
  {
    key: 'magic-link',
    name: 'Magic Link Login',
    description: 'Ein-Klick-Login ohne Passwort',
    endpoint: '/email-preview/magic-link',
  },
  {
    key: 'newsletter-welcome',
    name: 'Newsletter Willkommen',
    description: 'Begrüßung mit Rabattcode für neue Abonnenten',
    endpoint: '/email-preview/newsletter-welcome',
  },
];

export default function EmailTemplatePreview() {
  const [active, setActive] = useState(TEMPLATES[0].key);
  const [html, setHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('desktop'); // 'desktop' | 'mobile'

  const current = TEMPLATES.find((t) => t.key === active);

  const load = async () => {
    if (!current) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get(current.endpoint, {
        responseType: 'text',
        transformResponse: [(d) => d],
      });
      setHtml(res.data);
    } catch (err) {
      toast.error('Vorschau konnte nicht geladen werden.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [active]); // eslint-disable-line

  return (
    <div className="space-y-6" data-testid="email-template-preview-page">
      <AdminPageHeader
        title="E-Mail-Vorlagen"
        subtitle="Vorschau der versendeten E-Mails — Desktop & Mobile"
        icon={Mail}
      />

      <Link
        to="/admin/settings"
        className="inline-flex items-center gap-2 text-sm text-[#5D4E3C] hover:text-[#C4A084]"
        data-testid="back-to-settings"
      >
        <ArrowLeft className="h-4 w-4" />
        Zurück zu den Einstellungen
      </Link>

      {/* Template selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => (
              <Button
                key={t.key}
                variant={active === t.key ? 'default' : 'outline'}
                onClick={() => setActive(t.key)}
                data-testid={`template-btn-${t.key}`}
              >
                {t.name}
              </Button>
            ))}
          </div>
          {current && (
            <p className="mt-3 text-sm text-gray-600">{current.description}</p>
          )}
        </CardContent>
      </Card>

      {/* View toggle */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex border border-[#C4A084] rounded overflow-hidden">
          <button
            type="button"
            onClick={() => setView('desktop')}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              view === 'desktop' ? 'bg-[#5D4E3C] text-white' : 'bg-white text-[#5D4E3C]'
            }`}
            data-testid="view-toggle-desktop"
          >
            <Monitor className="h-4 w-4" />
            Desktop
          </button>
          <button
            type="button"
            onClick={() => setView('mobile')}
            className={`flex items-center gap-2 px-4 py-2 text-sm transition-colors ${
              view === 'mobile' ? 'bg-[#5D4E3C] text-white' : 'bg-white text-[#5D4E3C]'
            }`}
            data-testid="view-toggle-mobile"
          >
            <Smartphone className="h-4 w-4" />
            Mobile
          </button>
        </div>

        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          data-testid="reload-preview"
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Neu laden
        </Button>
      </div>

      {/* Preview frame */}
      <div className="flex justify-center bg-[#F5EDE4] p-4 sm:p-8 rounded">
        {loading ? (
          <div className="flex items-center gap-2 text-[#5D4E3C] py-16">
            <Loader2 className="h-5 w-5 animate-spin" />
            Lade Vorschau …
          </div>
        ) : (
          <iframe
            title="E-Mail Vorschau"
            srcDoc={html}
            sandbox="allow-same-origin"
            data-testid="email-preview-iframe"
            style={{
              width: view === 'mobile' ? 390 : '100%',
              maxWidth: view === 'mobile' ? 390 : 800,
              height: '900px',
              border: '1px solid #E8DCC8',
              background: '#FFFFFF',
            }}
          />
        )}
      </div>
    </div>
  );
}
